import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a convex/ directory import
import { api } from '@convex/_generated/api';
import { useCallback } from 'react';
import { toast } from 'sonner';
import type { TAiReplyPlatform } from '@/@types/aiReply';

export function useStyleAnalysis() {
  const profiles = useQuery(api['aiReply/styleProfile'].getAllPlatformProfiles);
  const updateProfileMutation = useMutation(api['aiReply/styleProfile'].updateProfile);
  const triggerAnalysisMutation = useMutation(api['aiReply/styleAnalysisTrigger'].triggerAnalysis);

  const triggerAnalysis = useCallback(
    async (platform: TAiReplyPlatform, lookbackDays?: number) => {
      try {
        await triggerAnalysisMutation({ platform, lookbackDays });
        toast.success(`Style analysis started for ${platform}`);
      } catch {
        toast.error('Failed to start style analysis');
      }
    },
    [triggerAnalysisMutation]
  );

  const updateProfile = useCallback(
    async (platform: TAiReplyPlatform, editedProfile: string) => {
      try {
        await updateProfileMutation({ platform, editedProfile });
        toast.success('Style profile updated');
      } catch {
        toast.error('Failed to update style profile');
      }
    },
    [updateProfileMutation]
  );

  const getProfileForPlatform = useCallback(
    (platform: TAiReplyPlatform) => {
      if (profiles === undefined) return undefined;
      return (
        profiles.find((p: NonNullable<typeof profiles>[number]) => p.platform === platform) ?? null
      );
    },
    [profiles]
  );

  return {
    profiles: profiles ?? [],
    isLoading: profiles === undefined,
    triggerAnalysis,
    updateProfile,
    getProfileForPlatform,
  };
}
